import type React from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Which model shoots the footage.
 *
 * ITS OWN CARD RATHER THAN A ROW IN THE TASK TABLE. Claude tasks are priced per
 * million tokens; these are priced per SECOND, and the number an operator has to
 * reason about is what one reel costs. Putting two incomparable price scales in
 * one table means neither can be read at a glance.
 *
 * THE PRICE IS SHOWN AS A REEL, NOT AS A RATE. "$0.57/sec" and "$0.063/sec" look
 * like small numbers next to each other; "$11.40 a reel" against "$1.26 a reel"
 * is the same fact and it is the one that changes a decision.
 *
 * Resolution is deliberately NOT a separate control. Each model has one tier
 * that is right for it and the server sets it on save — Gemini Omni's price is
 * flat across 360p/720p/1080p so anything lower is money for nothing, and
 * Seedance 1.5 Pro's cheap tier IS 720p. An operator choosing both would mostly
 * be choosing a way to overpay.
 */

interface VideoModel {
  provider: "kie" | "bfl";
  id: string;
  label: string;
  mode: "t2v" | "i2v";
  costPerSecondUsd: number;
  reel20sUsd: number;
  bestResolution: string;
  frame: string;
  minSeconds: number;
  maxSeconds: number;
  when: string;
  verified: boolean;
}

interface VideoModels {
  provider: string;
  fallbackProvider: string;
  resolution: string;
  selected: { t2v: string | null; i2v: string | null; t2vUnknown: boolean; i2vUnknown: boolean };
  t2v: VideoModel[];
  i2v: VideoModel[];
}

function ModeBlock({
  title,
  blurb,
  mode,
  models,
  selected,
  unknown,
  onPick,
  saving,
}: {
  title: string;
  blurb: React.ReactNode;
  mode: "t2v" | "i2v";
  models: VideoModel[];
  selected: string | null;
  unknown: boolean;
  onPick: (id: string | null) => void;
  saving: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-slate-900">{title}</h3>
        {/* Which variable is written depends on the vendor, so naming only the
            Kie one would be wrong half the time. */}
        <code className="text-[11px] text-slate-400">
          {selected?.startsWith("bfl/") ? "VIDEO_PROVIDER" : mode === "t2v" ? "KIE_VIDEO_MODEL_T2V" : "KIE_VIDEO_MODEL_I2V"}
        </code>
      </div>
      <p className="mt-0.5 text-xs text-slate-600">{blurb}</p>

      {unknown && (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          <code>{selected}</code> is set but is not in this list. The provider still sends it — it just has no price or
          guidance here, so nothing below is describing what actually runs.
        </div>
      )}

      <div className="mt-2 space-y-1.5">
        {models.map((m) => {
          const active = selected === m.id;
          return (
            <label
              key={m.id}
              className={`flex cursor-pointer items-start gap-3 rounded border p-2.5 ${
                active ? "border-slate-800 bg-slate-50" : "border-slate-200 hover:bg-slate-50"
              } ${saving ? "opacity-60" : ""}`}
            >
              <input
                type="radio"
                name={`video-model-${mode}`}
                className="mt-1"
                checked={active}
                disabled={saving}
                onChange={() => onPick(m.id)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-medium text-slate-900">{m.label}</span>
                  <span className="tabular-nums text-xs font-medium text-slate-700">
                    ${m.reel20sUsd.toFixed(2)} / 20s reel
                  </span>
                  <span className="tabular-nums text-[11px] text-slate-500">
                    ${m.costPerSecondUsd}/sec · {m.frame} · {m.minSeconds}–{m.maxSeconds}s
                  </span>
                  {/* "Verified" here means somebody generated a clip and watched
                      it. An unwatched model with a confident price is exactly the
                      thing this label exists to stop. */}
                  {/* Gift credits are the reason this option sits above cheaper
                      ones, so the badge has to say it or the ordering looks wrong. */}
                  {m.provider === "bfl" && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                      gift credits
                    </span>
                  )}
                  {!m.verified && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                      never generated
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{m.when}</div>
              </div>
            </label>
          );
        })}
        <label className="flex cursor-pointer items-center gap-3 rounded border border-slate-200 p-2.5 hover:bg-slate-50">
          <input
            type="radio"
            name={`video-model-${mode}`}
            checked={!selected}
            disabled={saving}
            onChange={() => onPick(null)}
          />
          <span className="text-xs text-slate-600">
            Unset — the provider falls back to its own default for this mode.
          </span>
        </label>
      </div>
    </div>
  );
}

export function VideoModelsCard() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery<VideoModels>({
    queryKey: ["models", "video"],
    queryFn: async () => {
      const r = await fetch("/api/models/video", { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const switchProvider = useMutation({
    mutationFn: async (provider: "bfl" | "kie") => {
      const r = await fetch("/api/models/video/provider", {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ provider }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return body;
    },
    onMutate: () => setErr(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["models", "video"] }),
    onError: (e: Error) => setErr(e.message),
  });

  const save = useMutation({
    mutationFn: async ({ mode, model }: { mode: "t2v" | "i2v"; model: string | null }) => {
      const r = await fetch("/api/models/video", {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ mode, model }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return body;
    },
    onMutate: () => setErr(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["models", "video"] }),
    onError: (e: Error) => setErr(e.message),
  });

  if (isLoading || !data) return <div className="text-sm text-slate-500">Loading video models…</div>;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Video models</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Which model shoots the footage for a reel. Priced per second, so the figure that matters is what a
          20-second reel costs — the spread between the cheapest and most expensive option here is 33×. Saving also
          sets the resolution to the right tier for that model.
        </p>
      </div>

      {data.provider === "replicate" && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <code>VIDEO_PROVIDER</code> is <code>replicate</code>, which none of these options use. Picking one below
          switches it.
        </div>
      )}

      {err && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}

      {/* THE VENDOR IS ONE SETTING, so it gets one control. It used to be
          implied by whichever model was picked last, which meant choosing FLUX
          for the opening shot and Seedance for the continuation left the vendor
          on whichever radio was clicked second. */}
      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-medium text-slate-900">Which vendor shoots</h3>
          <code className="text-[11px] text-slate-400">VIDEO_PROVIDER</code>
        </div>
        <p className="mt-0.5 text-xs text-slate-600">
          Every shot in a reel comes from this one vendor — the opening shot and every continuation. The model
          choices below configure Kie, which is used as the primary when Kie is selected and as the fallback when
          FLUX is.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {(["bfl", "kie"] as const).map((p) => (
            <button
              key={p}
              type="button"
              disabled={switchProvider.isPending}
              onClick={() => switchProvider.mutate(p)}
              className={`rounded border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                data.provider === p ? "border-slate-800 bg-slate-900 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {p === "bfl" ? "FLUX — gift credits first" : "Kie — paid account"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 rounded border border-slate-200 bg-white p-4 lg:grid-cols-2">
        <ModeBlock
          title="Opening shot (text → video)"
          blurb={
            data.provider === "bfl"
              ? "FLUX shoots this. The choice below is Kie's setting, used only if the gift credits run out mid-reel."
              : "Generated from the prompt alone. This is the shot that has to stop the scroll."
          }
          mode="t2v"
          models={data.t2v}
          selected={data.selected.t2v}
          unknown={data.selected.t2vUnknown}
          saving={save.isPending}
          onPick={(model) => save.mutate({ mode: "t2v", model })}
        />
        <ModeBlock
          title="Continuation shot (frame → video)"
          blurb={
            data.provider === "bfl"
              ? "FLUX shoots this too, from the previous shot's last frame. The choice below is Kie's fallback setting."
              : "Seeded with the previous shot's final frame, which is what keeps consecutive clips looking like one film."
          }
          mode="i2v"
          models={data.i2v}
          selected={data.selected.i2v}
          unknown={data.selected.i2vUnknown}
          saving={save.isPending}
          onPick={(model) => save.mutate({ mode: "i2v", model })}
        />
      </div>

      <p className="text-xs text-slate-500">
        Running on <span className="font-medium">{data.provider}</span>
        {data.fallbackProvider && data.fallbackProvider !== "none" ? (
          <>
            , falling back to <span className="font-medium">{data.fallbackProvider}</span> only when the credits run
            out — never on any other error, since a refused prompt fails the same way on both
          </>
        ) : (
          <> with no fallback: if the provider fails, the reel fails</>
        )}
        . Resolution is <span className="font-medium">{data.resolution}</span>, set from the chosen model.
      </p>
    </section>
  );
}
